import React, { useState, useCallback } from 'react';
import {
  Modal,
  Typography,
  Divider,
  Card,
  Space,
  Tag as TagAntd,
  Button,
  message,
  Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import {
  QuestionCircleOutlined,
  MailOutlined,
  MessageOutlined,
  CodeOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

const StyledTag = styled(TagAntd)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  max-width: 100%;
  transition: all 0.3s ease;

  .copy-button {
    opacity: 0;
    transition: opacity 0.2s ease;
    padding: 0;
    height: auto;
    
    &.copied {
      opacity: 1;
      color: #52c41a;
    }
  }

  &:hover {
    .copy-button {
      opacity: 1;
    }
  }
`;

const { Title, Paragraph, Text } = Typography;

const Tag = ({ children, ...props }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    // Convertir children a string de forma segura
    const textToCopy = typeof children === 'string'
      ? children
      : String(children);

    // Verificar si la API de clipboard está disponible
    if (!navigator.clipboard) {
      throw new Error('La función de copiar no está disponible en este navegador');
    }

    await navigator.clipboard.writeText(textToCopy);

    setCopied(true);
    message.success('Texto copiado al portapapeles');

    // Resetear el estado después de 2 segundos
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  }, [children]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledTag {...props}>
      {children}
      <Tooltip title={copied ? '¡Copiado!' : 'Copiar al portapapeles'}>
        <Button
          type="link"
          onClick={handleCopy}
          icon={copied ? <CheckOutlined /> : <CopyOutlined />}
          className={`copy-button ${copied ? 'copied' : ''}`}
          aria-label={copied ? 'Texto copiado' : 'Copiar texto al portapapeles'}
          size="small"
        />
      </Tooltip>
    </StyledTag>
  );
};

Tag.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
};

const ModalAyudaEstatus = ({ open, onCancel }) => (
  <Modal
    title={(
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <QuestionCircleOutlined style={{ color: '#1890ff' }} />
        <span>Guía de Variables Personalizadas para Reportes Morosos</span>
      </div>
    )}
    open={open}
    onCancel={onCancel}
    footer={null}
    centered
    width={900}
    bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
  >
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* Introducción */}
      <Card size="small" style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Paragraph style={{ margin: 0 }}>
          <Text strong>¿Qué es el Sistema de Variables Personalizadas?</Text>
          <br />
          Permite crear templates dinámicos para correos electrónicos y mensajes SMS que se
          personalizan automáticamente con los datos reales de cada reporte moroso.
        </Paragraph>
      </Card>

      {/* Campos Configurables */}
      <div>
        <Title level={4}>
          <MailOutlined style={{ marginRight: 8 }} />
          Campos Configurables
        </Title>
        <Paragraph>
          Los siguientes campos pueden contener variables dinámicas:
        </Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Tag color="blue" style={{ padding: '4px 12px', fontSize: '14px' }}>
            <strong>titulo_de_correo</strong>
            {' '}
            - Título del correo electrónico
          </Tag>
          <Tag color="green" style={{ padding: '4px 12px', fontSize: '14px' }}>
            <strong>contenido_de_correo</strong>
            {' '}
            - Contenido del correo electrónico
          </Tag>
          <Tag color="orange" style={{ padding: '4px 12px', fontSize: '14px' }}>
            <strong>contenido_de_mensaje</strong>
            {' '}
            - Contenido del mensaje SMS
          </Tag>
        </Space>
      </div>

      <Divider />

      {/* Sintaxis */}
      <div>
        <Title level={4}>
          <CodeOutlined style={{ marginRight: 8 }} />
          Sintaxis de Variables
        </Title>
        <Card size="small" style={{ backgroundColor: '#f0f2f5' }}>
          <Text code style={{ fontSize: '16px' }}>
            {'{modelo.campo"formato"}'}
          </Text>
        </Card>
        <Paragraph style={{ marginTop: 16 }}>
          <Text strong>Donde:</Text>
        </Paragraph>
        <ul>
          <li>
            <Text strong>modelo:</Text>
            {' '}
            ejecuciones_pae o estados_de_ejecucion_pae
          </li>
          <li>
            <Text strong>campo:</Text>
            {' '}
            nombre del campo del modelo
          </li>
          <li>
            <Text strong>formato:</Text>
            {' '}
            formato opcional (entre comillas)
          </li>
        </ul>
      </div>

      <Divider />

      {/* Campos de Ejecución PAE/REPORTE MOROSO */}
      <div>
        <Title level={4}> Campos de Reportes Morosos</Title>
        <Paragraph>
          Variables disponibles del modelo
          {' '}
          <Text code>ejecuciones_pae</Text>
          :
        </Paragraph>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px' }}>
          <Tag>folio</Tag>
          <Tag>monto_del_adeudo</Tag>
          <Tag>clave_principal_del_padron</Tag>
          <Tag>nombre_completo_del_contribuyente</Tag>
          <Tag>correo_electronico_del_contribuyente</Tag>
          <Tag>correo_electronico_del_padron</Tag>
          <Tag>correo_electronico_de_diligencia</Tag>
          <Tag>celular_del_contribuyente</Tag>
          <Tag>celular_del_padron</Tag>
          <Tag>celular_de_diligencia</Tag>
          <Tag>direccion_de_diligencia_text</Tag>
          <Tag>codigo_postal_de_diligencia</Tag>
          <Tag>clave_del_contribuyente</Tag>
          <Tag>rfc_del_contribuyente</Tag>
          <Tag>curp_del_contribuyente</Tag>
          <Tag>nombre_comercial_del_contribuyente</Tag>
          <Tag>telefono_del_contribuyente</Tag>
          <Tag>telefono_del_padron</Tag>
          <Tag>telefono_de_diligencia</Tag>
          <Tag>estatus_del_contribuyente</Tag>
          <Tag>observaciones_del_contribuyente</Tag>
          <Tag>clave_del_padron</Tag>
          <Tag>clave_secundaria_del_padron</Tag>
          <Tag>nombres_completos_del_padron</Tag>
          <Tag>estatus_del_padron</Tag>
          <Tag>fecha_de_obligacion_inicial_de_adeudos</Tag>
          <Tag>fecha_de_creacion</Tag>
          <Tag>ultima_fecha_de_modificacion</Tag>
        </div>
      </div>

      <Divider />

      {/* Campos de Estado de Ejecución PAE/REPORTE MOROSO */}
      <div>
        <Title level={4}> Campos de Estado de Reportes Morosos </Title>
        <Paragraph>
          Variables disponibles del modelo
          {' '}
          <Text code>estados_de_ejecucion_pae</Text>
          :
        </Paragraph>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px' }}>
          <Tag>fecha_de_creacion</Tag>
          <Tag>fecha_de_actuacion</Tag>
          <Tag>fecha_de_vencimiento</Tag>
          <Tag>observaciones</Tag>
          <Tag>clave_del_proceso</Tag>
          <Tag>reportes_id_impresos</Tag>
          <Tag>imprimio_reportes</Tag>
          <Tag>asigno_credito_fiscal</Tag>
          <Tag>envio_correo_electronico</Tag>
          <Tag>envio_mensaje_eletronico_sms</Tag>
          <Tag>asigno_ejecutor</Tag>
          <Tag>tiene_gastos_de_ejecucion</Tag>
          <Tag>impedir_cambiar_cartera_ejecutada</Tag>
        </div>
      </div>

      <Divider />

      {/* Formatos Disponibles */}
      <div>
        <Title level={4}> Formatos Disponibles</Title>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>

          <Card size="small" title="Formato Básico" style={{ borderColor: '#d9d9d9' }}>
            <Text code>{'{ejecuciones_pae.folio}'}</Text>
            <br />
            <Text type="secondary">Resultado: EF-25-000001</Text>
          </Card>

          <Card size="small" title="Formato de Moneda" style={{ borderColor: '#52c41a' }}>
            <Text code>{'{ejecuciones_pae.monto_del_adeudo"$moneda"}'}</Text>
            <br />
            <Text type="secondary">Resultado: $117,740.92</Text>
          </Card>

          <Card size="small" title="Formato en Letras" style={{ borderColor: '#1890ff' }}>
            <Text code>{'{ejecuciones_pae.monto_del_adeudo"LETRAS_CENTAVOS"}'}</Text>
            <br />
            <Text type="secondary">Resultado: Ciento diecisiete mil setecientos cuarenta pesos con noventa y dos centavos</Text>
          </Card>

          <Card size="small" title="Formato de Fecha" style={{ borderColor: '#fa8c16' }}>
            <Text code>{'{estados_de_ejecucion_pae.fecha_de_creacion"dd/mm/yyyy"}'}</Text>
            <br />
            <Text type="secondary">Resultado: 12/08/2025</Text>
          </Card>

        </Space>
      </div>

      <Divider />

      {/* Ejemplos Prácticos */}
      <div>
        <Title level={4}> Ejemplos Prácticos</Title>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          <Card
            size="small"
            title={(
              <span>
                <MailOutlined style={{ marginRight: 8 }} />
                Ejemplo: Título de Correo
              </span>
            )}
            style={{ borderColor: '#1890ff' }}
          >
            <Text code style={{ fontSize: '14px' }}>
              Notificación de Reporte Moroso - Folio:
              {' '}
              {'{ejecuciones_pae.folio}'}
            </Text>
          </Card>

          <Card
            size="small"
            title={(
              <span>
                <MailOutlined style={{ marginRight: 8 }} />
                Ejemplo: Contenido de Correo
              </span>
            )}
            style={{ borderColor: '#52c41a' }}
          >
            <div style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
              <Paragraph style={{ margin: 0, fontSize: '13px' }}>
                Estimado contribuyente,
                <br />
                <br />
                Se ha iniciado un proceso de reporte moroso con los siguientes datos:
                <br />
                <br />
                <strong>Folio:</strong>
                {' '}
                {'{ejecuciones_pae.folio}'}
                <br />
                <strong>Clave del padrón:</strong>
                {' '}
                {'{ejecuciones_pae.clave_principal_del_padron}'}
                <br />
                <strong>Monto del adeudo:</strong>
                {' '}
                {'{ejecuciones_pae.monto_del_adeudo"$moneda"}'}
                <br />
                <strong>Monto en letras:</strong>
                {' '}
                {'{ejecuciones_pae.monto_del_adeudo"LETRAS_CENTAVOS"}'}
                <br />
                <strong>Fecha:</strong>
                {' '}
                {'{estados_de_ejecucion_pae.fecha_de_creacion"dd/mm/yyyy"}'}
                <br />
                <strong>Dirección:</strong>
                {' '}
                {'{ejecuciones_pae.direccion_de_diligencia_text}'}
                <br />
                <br />
                Atentamente,
                <br />
                Sistema Integral de Recaudación
              </Paragraph>
            </div>
          </Card>

          <Card
            size="small"
            title={(
              <span>
                <MessageOutlined style={{ marginRight: 8 }} />
                Ejemplo: Mensaje SMS
              </span>
            )}
            style={{ borderColor: '#fa8c16' }}
          >
            <Text code style={{ fontSize: '14px' }}>
              SIR: Reporte Moroso
              {' '}
              {'{ejecuciones_pae.folio}'}
              {' '}
              - Adeudo:
              {' '}
              {'{ejecuciones_pae.monto_del_adeudo"moneda"}'}
              {' '}
              - Fecha:
              {' '}
              {'{estados_de_ejecucion_pae.fecha_de_creacion"dd/mm/yyyy"}'}
            </Text>
          </Card>

        </Space>
      </div>

      {/* Nota Importante */}
      <Card size="small" style={{ backgroundColor: '#fff7e6', borderColor: '#ffd591' }}>
        <Paragraph style={{ margin: 0 }}>
          <Text strong> Tip:</Text>
          {' '}
          Las variables se reemplazan automáticamente con los datos reales
          de cada reporte moroso al momento de enviar el correo o mensaje SMS.
        </Paragraph>
      </Card>

    </Space>
  </Modal>
);

ModalAyudaEstatus.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ModalAyudaEstatus;
